import type { ResizeHandle } from "@/lib/stores/canvas-store/canvas-resize";
import { Box, getBoxStyle, Resolution } from "./canvas-geometry";

export const RESIZE_CURSORS: Record<ResizeHandle, string> = {
  "top-left": "nwse-resize",
  "bottom-right": "nwse-resize",
  "top-right": "nesw-resize",
  "bottom-left": "nesw-resize",
  top: "ns-resize",
  bottom: "ns-resize",
  left: "ew-resize",
  right: "ew-resize",
};

const CORNERS: { handle: ResizeHandle; className: string }[] = [
  { handle: "top-left", className: "left-0 top-0" },
  { handle: "top-right", className: "left-full top-0" },
  { handle: "bottom-left", className: "left-0 top-full" },
  {
    handle: "bottom-right",
    className: "left-full top-full",
  },
];

const EDGES: { handle: ResizeHandle; className: string; pill: string }[] = [
  {
    handle: "top",
    className: "inset-x-0 top-0 h-2 -translate-y-1/2",
    pill: "h-2 w-5",
  },
  {
    handle: "bottom",
    className: "inset-x-0 top-full h-2 -translate-y-1/2",
    pill: "h-2 w-5",
  },
  {
    handle: "left",
    className: "inset-y-0 left-0 w-2 -translate-x-1/2",
    pill: "h-5 w-2",
  },
  {
    handle: "right",
    className: "inset-y-0 left-full w-2 -translate-x-1/2",
    pill: "h-5 w-2",
  },
];

type Props = {
  box: Box;
  resolution: Resolution;
  hasEdges: boolean;
  showEdgePills: boolean;
  activeHandle: ResizeHandle | null;
};

export default function ResizeHandles({
  box,
  resolution,
  hasEdges,
  showEdgePills,
  activeHandle,
}: Props) {
  const isShown = (handle: ResizeHandle) =>
    activeHandle === null || activeHandle === handle;
  const background = (handle: ResizeHandle) =>
    activeHandle === handle ? "bg-tertiary-400" : "bg-white";

  return (
    <div className="absolute" style={getBoxStyle(box, resolution)}>
      {/* Centers the handles on the 2px outline drawn inside the box. */}
      <div className="absolute inset-px">
        {hasEdges &&
          EDGES.filter(({ handle }) => isShown(handle)).map(
            ({ handle, className, pill }) => (
              <div
                key={handle}
                data-resize-handle={handle}
                style={{ cursor: RESIZE_CURSORS[handle] }}
                className={`group pointer-events-auto absolute flex items-center justify-center ${className}`}
              >
                {showEdgePills && (
                  <div
                    className={`pointer-events-none rounded-full border border-[#BCBCBC] group-hover:bg-tertiary-400 ${background(handle)} ${pill}`}
                  />
                )}
              </div>
            ),
          )}
        {CORNERS.filter(({ handle }) => isShown(handle)).map(
          ({ handle, className }) => (
            <div
              key={handle}
              data-resize-handle={handle}
              style={{ cursor: RESIZE_CURSORS[handle] }}
              className={`pointer-events-auto absolute size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#BCBCBC] hover:bg-tertiary-400 ${background(handle)} ${className}`}
            />
          ),
        )}
      </div>
    </div>
  );
}
